import React from 'react';
import { ICONS } from './constants';

export type WorkflowModuleKey = 'pillars' | 'commandCenter' | 'reportBuilder';

interface WorkflowGuidancePanelProps {
    isOpen: boolean;
    onClose: () => void;
    moduleKey: WorkflowModuleKey;
}

export const WorkflowGuidancePanel: React.FC<WorkflowGuidancePanelProps> = ({ isOpen, onClose, moduleKey }) => {
    
    if (!isOpen) return null;

    const moduleData: Record<WorkflowModuleKey, { title: string; subtitle: string; steps: { title: string; desc: string }[]; hints: string[] }> = {
        pillars: {
            title: 'Welcome to IntelOptics Insights',
            subtitle: 'Navigating Core Modules',
            steps: [
                { title: '1. Select Your Purpose', desc: 'Choose between Geopolitical Risk, Security Risk Management, Business Resilience, or Learning suites based on your immediate operational needs.' },
                { title: '2. Access the Command Center', desc: 'For an overarching view of all four pillars and your global live assets, click "Initialize Enterprise Control Center" at the top.' },
                { title: '3. Return to Main Dashboard', desc: 'You can swap workspaces (e.g., from Agency to Corporate) by clicking the "Change Workspace" button on the top status bar.' }
            ],
            hints: [
                'Each pillar contains highly specialized tactical applications tailored to intelligence gathering or physical security.',
                'Data generated in one pillar (like LMS certifications) may affect readiness metrics in another (like Security Risk Management).'
            ]
        },
        commandCenter: {
            title: 'Enterprise Control Center',
            subtitle: 'C2 Strategic Overview',
            steps: [
                { title: '1. Monitor Enterprise Health', desc: 'The top KPI cards provide immediate aggregate metrics across all active pillars (Threats, Projects, Compliance).' },
                { title: '2. Review the Live Map', desc: 'The Unified Map displays your registered Global Assets alongside active geopolitical alerts from the IntelOptics stream.' },
                { title: '3. Generate Reports', desc: 'Clicking "Generate Executive Report" allows you to compile the C2 data into predictive analytics suited for board-level presentations.' }
            ],
            hints: [
                'Ensure your Global Assets are correctly placed using the Asset Manager tool found in the standard Map module.',
                'Zulu Time is used universally across the dashboard to align global operational timelines.'
            ]
        },
        reportBuilder: {
            title: 'Executive Report Generation',
            subtitle: 'Methodologies & Forecasting',
            steps: [
                { title: '1. Select the Timeframe', desc: 'Aggregates historical data based on your chosen constraint (e.g., Year to Date, Trailing 12 Months).' },
                { title: '2. Scope the Pillars', desc: 'Choose which domains to cross-reference (e.g., disable LMS if focusing strictly on physical threat matrices).' },
                { title: '3. Choose Methodologies', desc: 'Toggle specific analytical frameworks (PESTLE, PMESII, CARVER) to include inside the finalized executive document.' }
            ],
            hints: [
                'CARVER Matrix: Evaluates structural and personnel targets based on Criticality, Accessibility, Recuperability, Vulnerability, Effect, and Recognizability.',
                'PMESII Analysis: Details operational environments across Political, Military, Economic, Social, Information, and Infrastructure domains.',
                'PESTLE Factors: Evaluates macro-environmental socio-economic forces.'
            ]
        }
    };

    const data = moduleData[moduleKey];

    return (
        <div className="fixed inset-0 z-[200] flex justify-end animate-fade-in">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm cursor-pointer" onClick={onClose} />
            <div className="relative w-full sm:w-[450px] h-full bg-[#0B1121] border-l border-blue-500/30 sm:rounded-l-2xl shadow-2xl flex flex-col transform transition-transform animate-slide-in-right overflow-hidden">
                
                {/* Header Sequence */}
                <div className="p-8 border-b border-slate-800 bg-gradient-to-br from-blue-900/30 to-black/40 flex justify-between items-start relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl"></div>
                    <div className="relative z-10">
                        <div className="flex items-center text-blue-400 mb-2">
                            <span className="bg-blue-500/20 p-2 rounded-lg mr-3 shadow-inner">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.829 1.508-2.336 1.53-.909 2.58-2.536 2.58-4.372a5.5 5.5 0 0 0-11 0c0 1.836 1.05 3.463 2.58 4.372.85.507 1.508 1.353 1.508 2.336V18" />
                                </svg>
                            </span>
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-400 font-mono">Workflow Guide</span>
                        </div>
                        <h2 className="text-2xl font-black text-white">{data.title}</h2>
                        <p className="text-blue-500 text-sm mt-1">{data.subtitle}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/10 transition-colors relative z-10">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-10 custom-scrollbar">
                    
                    {/* Standard Operating Procedures (Steps) */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6 flex items-center">
                            Standard Operating Procedure
                        </h3>
                        <div className="space-y-6">
                            {data.steps.map((step, idx) => (
                                <div key={idx} className="relative pl-6 border-l-2 border-slate-800 pb-2">
                                    <div className="absolute w-3 h-3 bg-blue-500 rounded-full -left-[7px] top-1 shadow-[0_0_10px_rgba(59,130,246,0.8)]"></div>
                                    <h4 className="text-base font-bold text-slate-200 mb-2">{step.title}</h4>
                                    <p className="text-sm text-slate-400 leading-relaxed bg-slate-800/20 p-3 rounded-lg border border-slate-800/50">{step.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Operational Hints */}
                    <div>
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center">
                            Tactical Hints & Intel
                        </h3>
                        <div className="space-y-3">
                            {data.hints.map((hint, idx) => (
                                <div key={idx} className="glass-panel p-4 border-l-4 border-l-amber-500 hover:border-l-blue-500 transition-colors group">
                                    <p className="text-sm text-slate-300 font-medium leading-relaxed group-hover:text-slate-200 transition-colors">
                                        <span className="font-bold text-amber-500 mr-2 group-hover:text-blue-500 transition-colors">TIP:</span>
                                        {hint}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="p-6 border-t border-slate-800 bg-[#060913] shrink-0">
                    <button 
                        onClick={onClose}
                        className="w-full py-4 px-4 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-bold uppercase tracking-widest text-xs transition-colors border border-slate-700 hover:border-slate-500"
                    >
                        Acknowledge & Close
                    </button>
                </div>
            </div>
        </div>
    );
};
